import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import Sticky from 'react-sticky-el'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import { Row, Col } from 'antd'
import moment from 'moment'
import { FormProvider, useForm } from 'react-hook-form'

import { contactRequestEventPayload, sanitizeFormValues } from 'components/ContactRequestFormComponents/helpers'
import Spinner from 'components/Spinner'
import Error from 'components/Error'
import ConditionalWrapper from 'components/ConditionalWrapper'
import PageHead from 'containers/PageHead'

import env from 'helpers/env'
import { pushDataLayer } from 'helpers/googleAnalytics'
import { setRequestedProvider } from 'store/modules/Providers'
import { isServer } from 'helpers/utilities'
import { pageTitle } from 'helpers/localization'
import storage from 'helpers/storage'
import { parseNextPath, buildUrl } from 'helpers/params'
import { usePreselectedFormData, setPreselectedFormData } from 'hooks/preselectedFormData'

import { STORAGE_KEY, PAYMENT_PENDING_PERIOD } from 'components/ContactRequestFormComponents/constants'

import { INITIAL_DATA_QUERY } from './graphql'

import ProviderOverview from './components/ProviderOverview'
import InitialContactRequest from './Initial'
import EventRoomContactRequest from './EventRoom'
import FormSwitcher from './FormSwitcher'
import NexusIntegratedDashboardFormSwitcher from './NexusIntegratedDashboardFormSwitcher'
import RedirectToNexus from './RedirectToNexus'

import 'components/ContactRequestFormComponents/shared.less'
import './index.less'

const stripe = loadStripe(env.require('STRIPE_PUBLISHABLE_API_KEY'))

const storeChangedValues = (allValues, preselectedFormData) => {
  if (isServer()) {
    return true
  }

  const idleCallback = window.requestIdleCallback || window.setTimeout

  idleCallback(() => {
    const currentValues = preselectedFormData

    setPreselectedFormData({
      ...currentValues,
      ...sanitizeFormValues(allValues),
    })
  })
}

const ContactRequest = (props) => {
  const {
    providerSlug,
  } = props

  const intl = useIntl()
  const dispatch = useDispatch()
  const isMobileUser = useSelector(state => state.layout.mobile)
  const router = useRouter()

  const form = useForm({
    mode: 'onChange',
    shouldUnregister: true,
  })
  const {
    data: initialData,
    loading: initialDataLoading,
  } = useQuery(INITIAL_DATA_QUERY, { variables: { providerSlug } })

  const [showLoading, setShowLoading] = useState(false)
  const { preselectedFormData } = usePreselectedFormData()

  const initialFieldValues = useMemo(() => {
    const { query } = parseNextPath(router?.asPath)

    return { ...storage.get(STORAGE_KEY), ...query }
  }, [router?.asPath])

  form.watch((values) => storeChangedValues(values, preselectedFormData))

  const provider = initialData?.provider || {}
  const currentUser = initialData?.currentUser

  if (initialDataLoading) {
    return <Spinner />
  }

  if (!provider.id) {
    return <Error statusCode="404" />
  }

  const userHasActiveNonEventRoomProjects = !!initialData?.projects.length
  const requestedDates = form.watch('requestedDates')
  const isEventRoom = provider.type === 'EventRoom'
  const requestedDate = Array.isArray(requestedDates) ? requestedDates[0] : requestedDates

  const isWithinPayablePeriod = !requestedDate ||
    moment(requestedDate).diff(moment(), 'days') < PAYMENT_PENDING_PERIOD

  const headerData = {
    title: pageTitle(intl, 'contactRequest.pageTitle', { providerName: provider.displayTitle }),
    metaRobots: 'noindex, follow',
  }

  let FormVariant = InitialContactRequest
  let customProps = {}

  if (isEventRoom) {
    FormVariant = EventRoomContactRequest
    customProps = {
      isWithinPayablePeriod,
    }
  } else if (userHasActiveNonEventRoomProjects &&
    initialData.currentUser?.preferredPartner?.settings?.isDashboardIntegratedWithNexus) {
    FormVariant = NexusIntegratedDashboardFormSwitcher
  } else if (userHasActiveNonEventRoomProjects) {
    FormVariant = FormSwitcher
    customProps = {
      projects: initialData.projects,
      initialData: { ...initialData, message: '', projectId: initialFieldValues.projectId },
    }

    if (router?.query.createNewProject !== undefined) {
      customProps.defaultMode = 'NEW_PROJECT'
    }

    if (currentUser.preferredPartner?.settings.hideProjectCrudUi) {
      customProps.defaultMode = 'ADD_TO_PROJECT'
    }
  } else if (initialData.currentUser?.preferredPartner?.settings?.isDashboardIntegratedWithNexus) {
    FormVariant = RedirectToNexus
  } else {
    FormVariant = InitialContactRequest
  }

  const markProviderAsRequested = (providerId) => dispatch(setRequestedProvider(providerId))

  const onSuccessCallback = (createContactRequestResult, disableSuccessForward) => {
    pushDataLayer(contactRequestEventPayload(createContactRequestResult))
    markProviderAsRequested(provider.id)

    if (disableSuccessForward) {
      setShowLoading(true)
    } else {
      const params = { contactRequestCreated: 1 }

      if (
        !createContactRequestResult.project.areProjectDetailsUpdated &&
        !createContactRequestResult.project.nexusProjectId
      ) {
        params.extendedRequestForm = true
      }

      window.location.href = buildUrl(`/projects/${createContactRequestResult.project.id}`, params)
    }
  }

  const additionalInformation = (
    <Sticky
      boundaryElement=".contact-request"
      disabled={isMobileUser}
      hideOnBoundaryHit={false}
      topOffset={isMobileUser ? 200 : 0}
      bottomOffset={10}
      stickyStyle={{ zIndex: 10 }}
      dontUpdateHolderHeightWhenSticky={true}
    >
      <ConditionalWrapper
        condition={!isMobileUser}
        wrapper={providerOverview => <a href={provider.path} target="_blank">{providerOverview}</a>}
      >
        <ProviderOverview
          provider={provider}
          initialFormValues={initialFieldValues}
        />
      </ConditionalWrapper>
    </Sticky>
  )

  const formVariant = (
    <ConditionalWrapper
      condition={provider.isDirectPayable && isWithinPayablePeriod}
      wrapper={formVariant => <Elements stripe={stripe}>{formVariant}</Elements>}
    >
      <FormVariant
        provider={provider}
        currentUser={currentUser}
        initialData={initialFieldValues}
        onSuccessCallback={onSuccessCallback}
        {...customProps}
      />
    </ConditionalWrapper>
  )

  return (
    <>
      {headerData && <PageHead headerData={headerData} />}

      <FormProvider {...form}>
        <div
          className={
            classNames(
              'contact-request',
              'contact-request-shared',
              { 'contact-request--mobile': isMobileUser },
            )
          }
        >
          {isMobileUser && (
            <Row gutter={[0, 12]} type="flex">
              <Col span={24}>
                {additionalInformation}
              </Col>
            </Row>
          )}

          <Row gutter={isMobileUser ? [0, 24] : 45} type="flex">
            <Col span={isMobileUser ? 24 : 14}>
              {showLoading ? <Spinner /> : formVariant}
            </Col>

            {!isMobileUser && (
              <Col span={10}>
                {additionalInformation}
              </Col>
            )}
          </Row>
        </div>
      </FormProvider>
    </>
  )
}

ContactRequest.propTypes = {
  providerSlug: PropTypes.string.isRequired,
}

export default ContactRequest
